/**
 * OAuthLandingPage — where a sign-in popup lands, for any flow on the
 * browser-bound finish (utils/oauthPopup.ts; items 65A, 66).
 *
 * The service's answer arrives in this page's address and is handed to the
 * CorpBrain window that started the sign-in; that window finishes it as its
 * own person and says how it ended, which this page shows, then closes. When
 * no window of this browser takes it — the link was opened somewhere else —
 * nothing is connected, and the page says where to start (it stays open to
 * be read). Tenant-less and session-less on purpose — except that a flow may
 * `finishHere` when no window took it: a consent popup carries its opener's
 * session, and a tab loaded before item 66 waits in the old protocol (88b).
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { handOverOAuthCallback, readOAuthCallback, type OAuthCallback, type OAuthOutcome } from '@/utils/oauthPopup';

/** What the page can show besides a flow's own outcomes. */
export type LandingStatus = 'finishing' | 'elsewhere' | 'error' | string;

/** How long the starting window has to exchange the code and answer. */
const FINISH_WAIT_MS = 60000;
/** How long a finished outcome stays on screen before the window closes itself. */
const CLOSE_AFTER_MS = 1200;

export function OAuthLandingPage({ icon, outcomes, title, closeHint, testId, finishHere }: {
  icon: ReactNode;
  /** The flow's own outcomes; anything else shows as 'error'. */
  outcomes: readonly string[];
  /** The heading for a status, with the service's name when the window said it. */
  title: (status: LandingStatus, name: string) => string;
  /** Shown when the browser would not let the window close itself. */
  closeHint: string;
  testId: string;
  /** No window of this browser took the answer: finish it in this page, as
   *  this tab's own person — null when it cannot (no session here). */
  finishHere?: (callback: OAuthCallback) => Promise<OAuthOutcome | null>;
}) {
  const callback = useMemo(() => readOAuthCallback(window.location.search), []);
  const [status, setStatus] = useState<LandingStatus>(callback ? 'finishing' : 'error');
  const [name, setName] = useState('');
  const [stillOpen, setStillOpen] = useState(false);

  useEffect(() => {
    if (!callback) return undefined;
    let live = true;
    const handing = handOverOAuthCallback(callback, FINISH_WAIT_MS, finishHere);
    void handing.result.then((result) => {
      if (!live) return;   // unmounted (StrictMode's first pass too): its cancel is not an answer
      if (result === null) { setStatus('elsewhere'); return; }
      setName(result.detail?.name ?? '');
      setStatus(outcomes.includes(result.outcome) ? result.outcome : 'error');
    });
    return () => { live = false; handing.cancel(); };
  }, [callback, outcomes, finishHere]);

  useEffect(() => {
    if (status === 'finishing' || status === 'elsewhere') return undefined;
    const close = window.setTimeout(() => {
      window.close();
      // Still here: the browser would not let this window close itself.
      window.setTimeout(() => setStillOpen(true), 300);
    }, CLOSE_AFTER_MS);
    return () => window.clearTimeout(close);
  }, [status]);

  return (
    <main className="google-access google-access--panel google-access--page" data-testid={testId} data-status={status}>
      <span className="google-access__logo" aria-hidden="true">{icon}</span>
      <h1 className="google-access__title" role="status">{title(status, name)}</h1>
      {stillOpen && <p className="google-access__body">{closeHint}</p>}
    </main>
  );
}
